import { wallClockInZone } from '../zonedTime';

/**
 * Document numbers (piece 22; decision 0002): one DocumentNumberSeries row per
 * numbered type, the counter taken inside the creating transaction, never
 * MAX(number) + 1. The rendering and the period are pure, so the allocation
 * service asserts them without a database behind it.
 */
export type NumberDateComponent = 'None' | 'YY' | 'YYYY' | 'YYYYMM';
export type NumberResetPolicy = 'Never' | 'Annually' | 'Monthly';

export interface NumberSeriesFormat {
  prefix: string;
  separator: string;
  dateComponent: NumberDateComponent;
  padding: number;
  resetPolicy: NumberResetPolicy;
}

/**
 * 🔨 The Check is not among them (decision 0556). Check stock is bought
 * pre-printed and pre-numbered, so the number on the paper is a fact before this
 * application has an opinion about it: a person types it once the batch has
 * printed, and no series allocates it.
 */
export const NUMBERED_DOCUMENT_TYPES = [
  'Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'WorkOrder', 'Bill', 'CreditNote', 'VendorCredit', 'ChangeOrder', 'RFQ',
  'Shipment', 'StockTransfer', 'StockCount', 'StockRevaluation', 'ExpenseReport', 'JournalEntry', 'Statement',
  // Decision 1730: the Package's own series, PKG.
  'Package',
] as const;
export type NumberedDocumentType = (typeof NUMBERED_DOCUMENT_TYPES)[number];

/** Whether a document type takes a number from the series at all; a Payment and a Refund do not (piece 22). */
export function isNumberedDocumentType(documentType: string): documentType is NumberedDocumentType {
  return (NUMBERED_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * The eighteen seeded rows: a short prefix each, the year, three digits, reset
 * yearly (piece 22), and the timing the plan decided — the Invoice and the
 * Statement at first send (decision 0002), every other type at creation.
 * 💰 The timing was seeded as Creation on all of them until 2026-09-14, when the
 * editor's suite read the Invoice row back. The Package's row joined on
 * 2026-10-04 (decision 1730).
 *
 * 💰 There were eighteen until 2026-09-18, when the Check's row went with
 * decision 0556; no seeded row carries the `Print` timing any more, and it stays
 * as configuration a later numbered type can ask for.
 */
export const DEFAULT_NUMBER_SERIES: readonly { documentType: NumberedDocumentType; prefix: string; allocateAt: 'Creation' | 'FirstSend' | 'Print' }[] = [
  { documentType: 'Quote', prefix: 'Q', allocateAt: 'Creation' }, { documentType: 'SalesOrder', prefix: 'SO', allocateAt: 'Creation' }, { documentType: 'Invoice', prefix: 'INV', allocateAt: 'FirstSend' },
  { documentType: 'PurchaseOrder', prefix: 'PO', allocateAt: 'Creation' }, { documentType: 'WorkOrder', prefix: 'WO', allocateAt: 'Creation' }, { documentType: 'Bill', prefix: 'BILL', allocateAt: 'Creation' },
  { documentType: 'CreditNote', prefix: 'CN', allocateAt: 'Creation' }, { documentType: 'VendorCredit', prefix: 'VC', allocateAt: 'Creation' }, { documentType: 'ChangeOrder', prefix: 'CO', allocateAt: 'Creation' },
  { documentType: 'RFQ', prefix: 'RFQ', allocateAt: 'Creation' }, { documentType: 'Shipment', prefix: 'SHP', allocateAt: 'Creation' }, { documentType: 'StockTransfer', prefix: 'ST', allocateAt: 'Creation' },
  { documentType: 'StockCount', prefix: 'SC', allocateAt: 'Creation' }, { documentType: 'StockRevaluation', prefix: 'SR', allocateAt: 'Creation' }, { documentType: 'ExpenseReport', prefix: 'EXP', allocateAt: 'Creation' },
  { documentType: 'JournalEntry', prefix: 'JE', allocateAt: 'Creation' }, { documentType: 'Statement', prefix: 'STMT', allocateAt: 'FirstSend' },
  { documentType: 'Package', prefix: 'PKG', allocateAt: 'Creation' },
];

/** The year and month the instant falls in, in the organization's own zone, so a number issued late on New Year's Eve carries the year it was issued in. */
function yearMonthOf(at: Date, zone: string): { year: string; month: string } {
  const wall = wallClockInZone(at, zone);
  return { year: wall.slice(0, 4), month: wall.slice(5, 7) };
}

/** The period the counter counts in: null when it never resets, YYYY or YYYYMM otherwise. A new key means the counter starts again at one. */
export function periodKeyOf(format: Pick<NumberSeriesFormat, 'resetPolicy'>, at: Date, zone: string): string | null {
  const { year, month } = yearMonthOf(at, zone);
  if (format.resetPolicy === 'Annually') return year;
  if (format.resetPolicy === 'Monthly') return `${year}${month}`;
  return null;
}

/** The rendered string, which is what uniqueness holds on: prefix, the date component when there is one, and the padded counter. */
export function renderDocumentNumber(format: NumberSeriesFormat, counter: number, at: Date, zone: string): string {
  const { year, month } = yearMonthOf(at, zone);
  const date = format.dateComponent === 'YY' ? year.slice(2) : format.dateComponent === 'YYYY' ? year : format.dateComponent === 'YYYYMM' ? `${year}${month}` : null;
  const digits = String(Math.max(1, Math.trunc(counter))).padStart(Math.max(0, format.padding), '0');
  return [format.prefix, date, digits].filter((part): part is string => part !== null && part !== '').join(format.separator);
}

/**
 * The Document Number Series editor (piece 15, specified; piece 22, built):
 * the pure half of every refusal the editor makes before a row is written.
 * 🔨 `nextNumber` moves forward only; a format change never renumbers a
 * document and is refused when its namespace already holds an issued number.
 */
export const NUMBER_DATE_COMPONENTS: readonly NumberDateComponent[] = ['None', 'YY', 'YYYY', 'YYYYMM'];
export const NUMBER_RESET_POLICIES: readonly NumberResetPolicy[] = ['Never', 'Annually', 'Monthly'];
export type NumberAllocateAt = 'Creation' | 'FirstSend' | 'Print';
export const NUMBER_ALLOCATE_AT: readonly NumberAllocateAt[] = ['Creation', 'FirstSend', 'Print'];
/** The most digits a counter pads to; wider than any sequence a business issues in a period. */
export const NUMBER_PADDING_CEILING = 9;

/** What the editor writes on a row: the six fields, the timing and the switch; `periodKey` is the allocator's alone. */
export interface NumberSeriesEdit extends NumberSeriesFormat {
  nextNumber: number;
  allocateAt: NumberAllocateAt;
  isActive: boolean;
}

/** The counter never moves backwards: a lower value would collide with a number already out in the world. */
export function nextNumberProblem(current: number, proposed: number): string | null {
  if (!Number.isInteger(proposed) || proposed < 1) return 'the next number is a whole number of one or more';
  if (proposed < current) return `the next number is ${current} and moves forward only; ${proposed} would reuse a number already issued`;
  return null;
}

/** The shape of a format before it is rendered: a prefix, a separator short enough to read, and a padding inside the ceiling. */
export function seriesFormatProblem(format: NumberSeriesFormat): string | null {
  if (format.prefix.trim().length === 0) return 'a series carries a prefix; type the letters every number in this series opens on';
  if (/\s/.test(format.prefix)) return 'a prefix carries no space; remove the spaces, or join the words with a dash';
  if (format.separator.length > 3) return 'a separator is three characters at most';
  if (!Number.isInteger(format.padding) || format.padding < 1 || format.padding > NUMBER_PADDING_CEILING) return `padding is a whole number from 1 to ${NUMBER_PADDING_CEILING}`;
  if (!NUMBER_DATE_COMPONENTS.includes(format.dateComponent)) return 'the date component is None, YY, YYYY or YYYYMM';
  if (!NUMBER_RESET_POLICIES.includes(format.resetPolicy)) return 'the reset policy is Never, Annually or Monthly';
  return null;
}

/** The rendered number with the counter removed: everything a document of this period shares. */
export function numberStem(format: NumberSeriesFormat, at: Date, zone: string): string {
  const probe = renderDocumentNumber(format, 1, at, zone);
  return probe.slice(0, probe.length - Math.max(1, format.padding));
}

/**
 * The first issued number the new format would share a namespace with, or null.
 * A number collides when it opens on the new stem and closes on a run of digits
 * at least the padding long, which is the shape of every number the format
 * renders. `I-00001` issued years ago refuses `INV` becoming `I` today; widening
 * the padding from three to five refuses nothing, because `INV-2026-001` has no
 * five-digit tail.
 */
export function formatCollision(issued: readonly string[], format: NumberSeriesFormat, at: Date, zone: string): string | null {
  const stem = numberStem(format, at, zone);
  const tail = new RegExp(`^\\d{${Math.max(1, format.padding)},}$`);
  for (const number of issued) {
    if (number.startsWith(stem) && tail.test(number.slice(stem.length))) return number;
  }
  return null;
}

/** True when any of the four format fields changed; the counter, the timing and the switch are not a format change. */
export function formatChanged(before: NumberSeriesFormat, after: NumberSeriesFormat): boolean {
  return before.prefix !== after.prefix || before.separator !== after.separator || before.dateComponent !== after.dateComponent || before.padding !== after.padding;
}

/** The audit row's changed fields: old value and new value, one entry per field that moved. */
export function seriesChangedFields(before: NumberSeriesEdit, after: NumberSeriesEdit): { field: string; from: unknown; to: unknown }[] {
  const fields: (keyof NumberSeriesEdit)[] = ['prefix', 'separator', 'dateComponent', 'padding', 'nextNumber', 'resetPolicy', 'allocateAt', 'isActive'];
  return fields.filter((field) => before[field] !== after[field]).map((field) => ({ field, from: before[field], to: after[field] }));
}
